import { useEffect, useRef, type RefObject } from 'react';

/** Surfaces that start calendar create (slot click / day click / drag-select). */
export function isCalendarCreateTarget(el: EventTarget | null): boolean {
  if (!(el instanceof Element)) return false;
  return Boolean(
    el.closest('[data-calendar-create-slot]') ||
      el.closest('.fc-daygrid-day') ||
      el.closest('.fc-daygrid-body') ||
      el.closest('.fc-timegrid-slot') ||
      el.closest('.fc-timegrid-body') ||
      el.closest('.fc-daygrid-day-frame') ||
      el.closest('.fc-scrollgrid')
  );
}

export function swallowCalendarCreateEvent(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  if (typeof (e as Event & { stopImmediatePropagation?: () => void }).stopImmediatePropagation === 'function') {
    (e as Event & { stopImmediatePropagation: () => void }).stopImmediatePropagation();
  }
}

/**
 * Block the rest of the same pointer gesture (mousedown → click) so closing a
 * menu over a calendar slot does not also open create-booking.
 */
export function installCalendarCreateGestureSwallow(durationMs = 400) {
  const types = ['mousedown', 'pointerup', 'mouseup', 'click', 'touchend'] as const;
  const listeners: Array<[string, EventListener]> = [];
  const cleanup = () => {
    for (const [type, listener] of listeners) {
      document.removeEventListener(type, listener, true);
    }
    window.clearTimeout(timer);
  };
  for (const type of types) {
    const listener: EventListener = (ev) => swallowCalendarCreateEvent(ev);
    listeners.push([type, listener]);
    document.addEventListener(type, listener, true);
  }
  const timer = window.setTimeout(cleanup, durationMs);
  return cleanup;
}

type OverlayRef = RefObject<HTMLElement | null> | RefObject<Element | null>;

/**
 * Close an overlay on outside pointer-down. If the outside target is a calendar
 * create surface, swallow the gesture so month/week select does not fire.
 *
 * Uses capture phase so this runs before WeekView slot pointerdown handlers.
 */
export function useDismissOverlayProtectingCalendarCreate(
  isOpen: boolean,
  onClose: () => void,
  contentRefs: OverlayRef[]
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const contentRefsRef = useRef(contentRefs);
  contentRefsRef.current = contentRefs;

  useEffect(() => {
    if (!isOpen) return;

    let dismissed = false;

    const onOutsideDown = (e: Event) => {
      const target = e.target as Node | null;
      if (!target) return;

      for (const ref of contentRefsRef.current) {
        if (ref.current?.contains(target)) return;
      }

      const onSlot = isCalendarCreateTarget(e.target);

      if (dismissed) {
        if (onSlot) swallowCalendarCreateEvent(e);
        return;
      }

      dismissed = true;
      onCloseRef.current();

      if (onSlot) {
        swallowCalendarCreateEvent(e);
        installCalendarCreateGestureSwallow();
      }
    };

    document.addEventListener('pointerdown', onOutsideDown, true);
    document.addEventListener('mousedown', onOutsideDown, true);
    document.addEventListener('touchstart', onOutsideDown, true);

    return () => {
      dismissed = true;
      document.removeEventListener('pointerdown', onOutsideDown, true);
      document.removeEventListener('mousedown', onOutsideDown, true);
      document.removeEventListener('touchstart', onOutsideDown, true);
    };
  }, [isOpen]);
}
